import { useState, useEffect, useRef } from 'react';
import { Phone } from 'lucide-react';

const pillars = [
  {
    title: "Always There",
    gradient: "from-[#4E9CFF] to-[#FF4DB8]",
    description: "Present day and night, on every channel your customers already use.",
  },
  {
    title: "Always Evolving",
    gradient: "from-[#4E9CFF] to-[#FF4DB8]",
    description: "Every conversation captured, analysed, and turned into your next best step.",
  },
  {
    title: "Always On-Brand",
    gradient: "from-[#4E9CFF] to-[#FF4DB8]",
    description: "Every response speaks in your voice, never a generic script.",
  },
];

const TypewriterPillar = ({
  pillar,
  isActive,
  isComplete,
  onComplete
}: {
  pillar: typeof pillars[0];
  isActive: boolean;
  isComplete: boolean;
  onComplete: () => void;
}) => {
  const [displayText, setDisplayText] = useState('');
  const [showDescription, setShowDescription] = useState(false);
  const typingSpeed = 30;

  useEffect(() => {
    if (!isActive || isComplete) return;

    const targetText = pillar.title;
    let currentIndex = 0;

    const typeInterval = setInterval(() => {
      if (currentIndex < targetText.length) {
        setDisplayText(targetText.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typeInterval);
        setShowDescription(true);
        setTimeout(onComplete, 300);
      }
    }, typingSpeed);

    return () => clearInterval(typeInterval);
  }, [isActive, isComplete, pillar.title, onComplete]);

  // If complete (from a previous session or animation finished), show full content
  useEffect(() => {
    if (isComplete) {
      setDisplayText(pillar.title);
      setShowDescription(true);
    }
  }, [isComplete, pillar.title]);

  // Format display text with line break for "Always There"
  const formattedText = pillar.title === "Always There"
    ? displayText.replace("Always ", "Always\n")
    : displayText;

  return (
    <div className="text-center p-4 sm:p-6 group hover:scale-105 transition-transform duration-300">
      <h3 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${pillar.gradient} mb-3 sm:mb-4 drop-shadow-lg whitespace-pre-line min-h-[2.5em] sm:min-h-[2.8em] md:min-h-[3em] flex items-center justify-center`}>
        {formattedText}
        {isActive && !isComplete && <span className="animate-pulse text-white/80">|</span>}
      </h3>
      <p className={`text-gray-100 text-sm sm:text-base md:text-lg drop-shadow-md transition-opacity duration-500 ${showDescription ? 'opacity-100' : 'opacity-0'}`}>
        {pillar.description}
      </p>
    </div>
  );
};

export default function HomeCTA() {
  const [activePillarIndex, setActivePillarIndex] = useState(-1); // Start at -1, animation begins when in view
  const [completedPillars, setCompletedPillars] = useState<number[]>([]);
  const [hasStarted, setHasStarted] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Start animation when section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted) {
          setHasStarted(true);
          setActivePillarIndex(0);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasStarted]);

  const handlePillarComplete = (index: number) => {
    setCompletedPillars(prev => [...prev, index]);
    if (index < pillars.length - 1) {
      setActivePillarIndex(index + 1);
    }
  };

  return (
    <section id="lifecycle-support" className="relative w-full py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/home-page-imagery/cta-genie-live.webp"
          alt="Bistro owner in conversation with her Genie on her phone at closing time"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlay for text contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 1. Main Title & Subtitle */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4 sm:mb-6 drop-shadow-2xl">
            Now we're talking.
          </h2>
          <p className="mt-3 sm:mt-4 text-base sm:text-lg md:text-xl leading-relaxed sm:leading-8 text-gray-100 max-w-3xl mx-auto drop-shadow-lg px-2 sm:px-0">
            Real-world support. Reinvented. One genie, every channel, in your voice, live within the hour.
          </p>
        </div>

        {/* 2. Three Pillars - Mobile Optimized with Typewriter Animation */}
        <div ref={sectionRef} className="mb-12 sm:mb-16 md:mb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 md:gap-10 max-w-6xl mx-auto">
            {pillars.map((pillar, index) => (
              <TypewriterPillar
                key={pillar.title}
                pillar={pillar}
                isActive={activePillarIndex === index}
                isComplete={completedPillars.includes(index)}
                onComplete={() => handlePillarComplete(index)}
              />
            ))}
          </div>
        </div>
        
        {/* CTA Block */}
        <div className="text-center mt-4 sm:mt-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 md:gap-6 px-4 sm:px-0">
            <a
              href="/signup"
              className="px-6 sm:px-8 md:px-10 py-3.5 sm:py-4 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-sm sm:text-base md:text-lg w-full sm:w-auto text-center max-w-[280px] sm:max-w-none min-h-[48px] touch-manipulation"
              style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
            >
              Start free
            </a>
            <a
              href="/live-agent/business-onboarding"
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 md:px-10 py-3.5 sm:py-4 bg-transparent hover:bg-white/10 active:bg-white/20 text-white hover:text-gray-100 font-semibold rounded-xl border-2 border-white/50 hover:border-white/70 transition-all duration-300 text-sm sm:text-base md:text-lg w-full sm:w-auto text-center backdrop-blur-sm max-w-[280px] sm:max-w-none min-h-[48px] touch-manipulation"
            >
              <Phone className="h-5 w-5" />
              Talk to a live genie
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}